<template>
  <div class="sender mb-2" :class="{'receiver': isUser}">
    <b-avatar 
      class="avatar" 
      :src="isUser ? avatars.user : botAvatar" 
    />
    <div class="w-100 ml-2" :class="{'d-flex flex-column align-items-end mr-2': isUser}">
      <!-- Actions header for assistant messages -->
      <div class="title-conversation w-100">
        <div v-if="isAssistant && !isLoading" class="d-flex justify-content-between align-items-start">
          <span 
            v-if="!messageType" 
            class="avenir-medium cursor-pointer d-block" 
            @click="$emit('copy')"
          >
            {{ $t('ia.chat.copy') }}
          </span>
          <span v-else class="d-block" />
          <slot name="actions" />
        </div>
      </div>
      
      <!-- Message content -->
      <div class="message-bubble">
        <!-- Loading state -->
        <div v-if="isLoading && !messageType">
          <div class="thinking-line">
            <span class="thinking-text">{{ $t('ia.chat.thinking') }} ...</span>
            <span class="thinking-dot" aria-hidden="true" />
          </div>
        </div>
        
        <!-- Text message -->
        <div v-else-if="!messageType" class="typewriter">
          {{ content }}
        </div>
        
        <!-- Image grid skeleton -->
        <div v-else-if="isLoading && messageType === 'images'" class="row">
          <div
            v-for="n in 4"
            :key="'skeleton-' + n"
            class="container-aspect-ratio col-12 col-sm-6 col-md-3 mb-1"
          >
            <b-skeleton-img aspect="1:1" class="w-100 h-100" />
          </div>
        </div>

        <!-- Image grid -->
        <div v-else class="row">
          <div
            v-for="(imgUrl, index) in content"
            :key="index"
            class="container-aspect-ratio col-12 col-sm-6 col-md-3 mb-1"
          >
            <div class="h-100 aspect-ratio">
              <div class="w-100 h-100 position-relative">
                <div class="container-dropdown-image" @click="downloadAction(imgUrl)">
                  <feather-icon icon="DownloadIcon" class="text-white" />
                </div>
                <img :src="imgUrl" class="img-ia" />
                <div class="container-ia-svg">
                  <img :src="require('@/assets/images/icons/ia.svg')" alt="ia" class="ia-svg-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BAvatar, 
  BSkeletonImg 
} from 'bootstrap-vue';
import { getAssetUrl } from '@/libs/utils/urls';

export default {
  name: 'ChatMessageBubble',
  components: { BAvatar, 
    BSkeletonImg 
  },
  props: {
    role: { type: String, required: true },
    content: { type: [String, Array], required: true },
    messageType: { type: String, default: null },
    isLoading: { type: Boolean, default: false },
    avatars: { type: Object, required: true },
  },
  computed: {
    isUser() { return this.role === 'user'; },
    isAssistant() { return this.role === 'assistant'; },
    botAvatar() {
      const assistant = this.avatars.assistant;
      if (!assistant) return require('@/assets/images/icons/chat-bot.svg');

      const image_assistant = assistant.image ? getAssetUrl(assistant.image) : assistant.image_url
      return image_assistant || require('@/assets/images/icons/chat-bot.svg');
    },  
  },
  methods: {
    downloadAction(fileUrl) {
      this.$emit('download', [fileUrl]);
    },
  },
}
</script>

<style scoped lang="scss">
.typewriter {
  word-break: break-word;
  overflow-wrap: break-word;
  min-height: 1em;
}

.thinking-line {
  display: inline-flex; 
  align-items: center; 
  gap: .4rem;
}

.thinking-text {
  display: inline-block;
  font-family: 'avenir-medium';
  background-image: linear-gradient(90deg, #000000 25%, #acafb7 50%, #d8e0f2 75%, #9c9fa6 85%, #252424 95%);
  background-size: 200% 100%;
  background-position: 0 0;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: shimmer 2.5s linear infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.thinking-dot {
  width: 8px; 
  height: 8px; 
  border-radius: 50%;
  background: #70747c;
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(.8); opacity: .6; }
  50% { transform: scale(1.25); opacity: 1; }
}

.sender {
  display: flex;
  width: 100%;
  
  .avatar {
    width: 42px;
    height: 42px;
    border-radius: 100%;
    background-color: #E9ECEF;
    flex-shrink: 0;
  }
  
  .title-conversation {
    width: 95% !important;
    height: 20px;
    margin-bottom: 0.4em;
  }
  
  .message-bubble {
    width: 95%;
    height: auto;
    background-color: #e9ecef;
    border-radius: .5em;
    position: relative;
    color: black;
    font-family: 'avenir-medium';
    padding: 1.6em;
    white-space: pre-line;

    &::before {
      border: 10px solid #e9ecef;
      content: '';
      border-left-color: transparent;
      border-bottom-color: transparent;
      border-right-color: transparent;
      position: absolute;
      top: 0;
      left: -10px;
    }
  }
}

.receiver {
  flex-direction: row-reverse;
  
  .message-bubble {
    background-color: #55a6c4;
    color: white;
    
    &::after {
      border: 10px solid #55a6c4 !important;
      content: '' !important;
      border-left-color: transparent !important;
      border-bottom-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent;
      position: absolute !important;
      right: -10px !important;
      top: 0;
    }
    
    &::before {
      border: 10px solid transparent !important;
      content: '' !important;
    }
  }
}

.container-dropdown-image {
  background-color: rgba(0, 0, 0, 0.699);
  position: absolute;
  top: 5%;
  border-radius: 50%;
  padding: 0.1rem;
  right: 5%;
  flex-shrink: 0;
  transform: translateX(70px);
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
  }
}

.container-aspect-ratio {
  &:hover .container-dropdown-image {
    animation: dropdown-animation 300ms forwards;
  }
  &:not(:hover) .container-dropdown-image {
    animation: dropdown-animation-back 300ms forwards;
  }
}

@keyframes dropdown-animation {
  100% { transform: translateX(0); opacity: 1; }
}

@keyframes dropdown-animation-back {
  0% { transform: translateX(0px); }
  100% { transform: translateX(50px); opacity: 0; }
}

.aspect-ratio {
  background: linear-gradient(0deg, rgba(250,250,250,1) 0%, rgba(219,219,219,1) 100%);
  display: flex;
  aspect-ratio: 1/1;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  
  .img-ia {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .container-ia-svg {
    position: absolute;
    bottom: 5%;
    right: 5%;
    display: flex;
    justify-content: center;
    align-items: center;
    
    .ia-svg-icon {
      width: 30px;
      height: 30px;
    }
  }
}
</style>
